import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { HiOutlineX } from 'react-icons/hi';
import HelpPopover from './HelpPopover';
import HelpProgress from './HelpProgress';
import { useHelp } from '../context/useHelp';

const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

const HelpTour = ({ returnFocusElement }) => {
    const {
        beginOrientationInteraction,
        currentStep,
        currentStepIndex,
        dismissOrientation,
        nextOrientationStep,
        pauseOrientation,
        previousOrientationStep,
        target,
        totalSteps,
    } = useHelp();
    const dialogRef = useRef(null);
    const closeButtonRef = useRef(null);
    const isLastStep = currentStepIndex === totalSteps - 1;

    useEffect(() => {
        closeButtonRef.current?.focus();
    }, [currentStepIndex]);

    useEffect(() => () => {
        if (returnFocusElement?.isConnected) {
            returnFocusElement.focus();
            return;
        }

        window.setTimeout(() => {
            document.querySelector('[data-help-invitation-primary]')?.focus();
        }, 0);
    }, [returnFocusElement]);

    useEffect(() => {
        const appRoot = document.getElementById('root');
        const previousOverflow = document.body.style.overflow;
        const previousAriaHidden = appRoot?.getAttribute('aria-hidden');
        const previouslyInert = appRoot?.inert;

        document.body.style.overflow = 'hidden';
        if (appRoot) {
            appRoot.inert = true;
            appRoot.setAttribute('aria-hidden', 'true');
        }

        return () => {
            document.body.style.overflow = previousOverflow;
            if (!appRoot) return;
            appRoot.inert = Boolean(previouslyInert);
            if (previousAriaHidden === null) {
                appRoot.removeAttribute('aria-hidden');
            } else {
                appRoot.setAttribute('aria-hidden', previousAriaHidden);
            }
        };
    }, []);

    const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            pauseOrientation();
            return;
        }
        if (event.key !== 'Tab') return;

        const focusable = [...dialogRef.current.querySelectorAll(FOCUSABLE_SELECTOR)];
        if (focusable.length === 0) {
            event.preventDefault();
            dialogRef.current.focus();
            return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    };

    return createPortal(
        <HelpPopover dialogRef={dialogRef} placement={currentStep.placement} target={target}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="help-tour-title"
                aria-describedby="help-tour-description"
                tabIndex="-1"
                onKeyDown={handleKeyDown}
            >
                <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                        <HelpProgress current={currentStepIndex + 1} total={totalSteps} />
                    </div>
                    <button
                        ref={closeButtonRef}
                        type="button"
                        onClick={pauseOrientation}
                        aria-label="Pause tutorial"
                        className="inline-flex h-11 w-11 flex-none items-center justify-center rounded-lg text-text-secondary-reg hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                    >
                        <HiOutlineX aria-hidden="true" className="text-xl" />
                    </button>
                </div>
                <h2 id="help-tour-title" className="mt-5 text-xl font-semibold text-text-primary">
                    {currentStep.title}
                </h2>
                <p id="help-tour-description" className="mt-2 leading-7 text-text-secondary-dark">
                    {currentStep.description}
                </p>
                <p className="sr-only" aria-live="polite">
                    Step {currentStepIndex + 1} of {totalSteps}: {currentStep.title}
                </p>
                <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-4">
                    <button
                        type="button"
                        onClick={dismissOrientation}
                        className="inline-flex min-h-11 items-center justify-center rounded-lg px-3 py-2 text-sm font-medium text-text-secondary-reg hover:bg-red-50 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                    >
                        Skip tutorial
                    </button>
                    <div className="flex flex-wrap justify-end gap-2">
                        {currentStep.practice && (
                            <button
                                type="button"
                                onClick={() => beginOrientationInteraction('practice')}
                                className="inline-flex min-h-11 items-center justify-center rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2 font-semibold text-indigo-800 hover:bg-indigo-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
                            >
                                {currentStep.practice.actionLabel}
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={previousOrientationStep}
                            disabled={currentStepIndex === 0}
                            className="inline-flex min-h-11 items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 font-semibold text-text-secondary-dark hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:text-gray-300"
                        >
                            Back
                        </button>
                        <button
                            type="button"
                            onClick={currentStep.gate
                                ? () => beginOrientationInteraction('gate')
                                : nextOrientationStep}
                            className="inline-flex min-h-11 items-center justify-center rounded-lg bg-normative-blue px-4 py-2 font-semibold text-white hover:bg-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
                        >
                            {isLastStep
                                ? 'Finish tutorial'
                                : currentStep.gate?.actionLabel || currentStep.nextLabel || 'Next'}
                        </button>
                    </div>
                </div>
            </div>
        </HelpPopover>,
        document.body
    );
};

export default HelpTour;
